import { useState, useCallback } from 'react';
import { getCart, addCart, updateCart, deleteCart, getCartSummary, getCartSummaryByBusiness } from 'helpers/backend_helper';

export interface CartItem {
    id?: number; // Optional id to track if item exists in backend
    imageUrl?: string;
    variantId: number;
    productId: number;
    sku: string;
    productName: string;
    brandName: string;
    categoryName: string;
    sizeNames: string;
    colors: string[];
    unitPrice: string;
    packOf: number;
    discountPercent: string;
    discountValue: string;
    inventory: number;
    quantity: number;
}

export interface CartSummary {
    totalProducts: number;
    totalQuantity: number;
    subTotal: number;
    totalDiscount: number;
    gst: number;
    shipping: number;
    total: number;
}

interface UseCartReturn {
    cartItems: CartItem[];
    cartSummary: CartSummary | null;
    isLoading: boolean;
    error: string | null;
    addItem: (product: Omit<CartItem, 'quantity' | 'id'>) => void;
    updateItem: (variantId: number, quantity: number) => void;
    updateItemPrice: (variantId: number, unitPrice: string) => void;
    removeItem: (variantId: number) => void;
    refreshCart: () => Promise<void>;
    clearCart: () => void;
    syncCartWithBackend: () => Promise<void>;
    calculateLocalSummary: () => CartSummary;
}

export const useCart = (userId: number, businessId: number): UseCartReturn => {
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const [cartSummary, setCartSummary] = useState<CartSummary | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Load initial cart data
    const refreshCart = useCallback(async () => {
        if (!businessId) return;

        setIsLoading(true);
        setError(null);
        try {
            const cartResponse = await getCart(userId, businessId);

            if (cartResponse?.data) {
                const existingItems = cartResponse.data.map((item: any) => {
                    return {
                        id: item.id,
                        variantId: item.productVariantId,
                        productId: item.productId,
                        sku: item?.sku || '',
                        productName: item?.productName || '',
                        brandName: item?.brandName || '',
                        categoryName: item?.categoryName || '',
                        sizeNames: item?.size || '',
                        colors: item?.color || '',
                        unitPrice: item?.unitPrice || '',
                        packOf: item.packOf,
                        discountPercent: item.discountPercent,
                        discountValue: item.discountValue,
                        inventory: 0,
                        quantity: item.quantity,
                        imageUrl: item.imageUrl
                    }
                });
                
                setCartItems(existingItems);
            }
        } catch (err) {
            console.error('Error refreshing cart:', err);
            setError('Failed to refresh cart');
        } finally {
            setIsLoading(false);
        }
    }, [userId, businessId]);

    const refreshCartSummary = useCallback(async () => {
        if (!businessId) return;
        const summaryResponse = await getCartSummaryByBusiness(userId, businessId);
        console.log(summaryResponse);
        if (summaryResponse?.data) {
            setCartSummary(summaryResponse.data[0] as any);
            console.log(cartSummary);
        }
    }, [userId, businessId]);



    // Local cart management functions
    const addItem = useCallback((product: Omit<CartItem, 'quantity' | 'id'>) => {
        setCartItems(prevItems => {
            const existingItem = prevItems.find(item => item.variantId === product.variantId);
            if (existingItem) {
                // If item exists, increment quantity if inventory allows
                if (existingItem.quantity < product.inventory) {
                    return prevItems.map(item =>
                        item.variantId === product.variantId
                            ? { ...item, quantity: item.quantity + 1 }
                            : item
                    );
                }
                return prevItems;
            }
            // If item doesn't exist, add new item with quantity 1 and id: 0
            return [{ ...product, quantity: 1, id: 0 },...prevItems];
        });
        setCartSummary(null);
    }, []);

    const updateItem = useCallback((variantId: number, quantity: number) => {
        setCartItems(prevItems =>
            prevItems.map(item => {
                if (item.variantId === variantId) {
                    return { ...item, quantity };
                }
                return item;
            }).filter(item => item.quantity > 0)
        );
        setCartSummary(null);
    }, []);

    const updateItemPrice = useCallback((variantId: number, unitPrice: string) => {
        setCartItems(prevItems =>
            prevItems.map(item => {
                if (item.variantId === variantId) {
                    return { ...item, unitPrice };
                }
                return item;
            })
        );
        setCartSummary(null);
    }, []);

    const removeItem = useCallback((variantId: number) => {
        setCartItems(prevItems => prevItems.filter(item => item.variantId !== variantId));
        setCartSummary(null); // Force summary to be recalculated from cartItems (localSummary)
    }, []);

    const clearCart = useCallback(() => {
        setCartItems([]);
        setCartSummary(null);
        setError(null);
    }, []);

    // Calculate local cart summary
    const calculateLocalSummary = useCallback((): CartSummary => {
        const subtotal = cartItems.reduce((total, item) => {
            const unitPrice = parseFloat(item.unitPrice || '0');
            const discountValue = parseFloat(item.discountValue || '0');
            const netPrice = unitPrice - discountValue;
            return total + (netPrice * item.quantity * item.packOf);
        }, 0);

        const tax = (subtotal * 5) / 100; // Assuming 5% tax rate
        const itemDiscounts = cartItems.reduce((total, item) => 
            total + (parseFloat(item.discountValue || '0') * item.quantity * item.packOf), 0);
        const shippingCharge = 250; // Fixed shipping charge
        const total = subtotal + tax + shippingCharge;

        return {
            totalProducts: cartItems.length,
            totalQuantity: cartItems.reduce((total, item) => total + item.quantity, 0),
            subTotal: subtotal,
            totalDiscount: itemDiscounts,
            gst: tax,
            shipping: shippingCharge,
            total: total
        };
    }, [cartItems]);

    // Sync cart with backend during checkout
    const syncCartWithBackend = useCallback(async () => {
        if (!businessId) return;

        setIsLoading(true);
        setError(null);
        try {
            // Process each item in the cart
            for (const item of cartItems) {
                if (item.id === 0) {
                    // New item - add to cart
                    await addCart({
                        businessId,
                        productId: item.productId,
                        productVariantId: item.variantId,
                        quantity: item.quantity,
                        unitPrice: item.unitPrice
                    });
                } else if (item.id) {
                    // Existing item - update quantity and unit price
                    await updateCart({
                        id: item.id,
                        quantity: item.quantity,
                        unitPrice: item.unitPrice
                    });
                }
            }

            // Remove items that were deleted locally
            // const existingItems = await getCart(userId, businessId);
            // const existingIds = existingItems.data.map((item: CartItem) => item.id);
            // const currentIds = cartItems.map(item => item.id).filter(id => id !== 0);
            // if (currentIds?.length > 0) 
            //     {
            //         for (const id of existingIds) {
            //             if (!currentIds.includes(id)) {
            //                 await deleteCart(id);
            //             }
            //         }
            //     }

            // Refresh cart after all operations
            await refreshCart();
            await refreshCartSummary();
        } catch (err) {
            console.error('Error syncing cart with backend:', err);
            setError('Failed to sync cart with backend');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [businessId, cartItems, userId, refreshCart]);

    return {
        cartItems,
        cartSummary,
        isLoading,
        error,
        addItem,
        updateItem,
        updateItemPrice,
        removeItem,
        refreshCart,
        clearCart,
        syncCartWithBackend,
        calculateLocalSummary
    };
}; 